import { useCallback, useEffect, useState } from 'react';
import { ListProvider } from '../models/interfaces/ListProvider';
import { Resolver } from '../models/interfaces/Resolver';
import { LOAD_TIMEOUT_MS } from './loading';
import { startHomeLoad } from './homeLoadTiming';

interface State<T> {
    provider?: ListProvider<Resolver<T>>;
    items: T[];
    slots?: { id: string; item?: T }[];
    firstId?: string;
    loading: boolean;
    error: boolean;
}

const EMPTY_ITEMS = [];

/** Publish healthy items immediately; stop accepting late responses after cleanup or timeout. */
export function useResolverList<T>(provider: ListProvider<Resolver<T>> | undefined, label: string) {
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState<State<T>>({ items: [], loading: true, error: false });
    const retry = useCallback(() => setAttempt(value => value + 1), []);

    useEffect(() => {
        if (!provider) return;
        let active = true;
        let slots: { id: string; item?: T }[] | undefined;
        let failed = false;
        let firstId: string | undefined;
        const items = new Map<number, T>();
        const timing = startHomeLoad(label, attempt);
        setState({ provider, items: [], loading: true, error: false });
        const publish = (loading: boolean) => {
            if (!active) return;
            setState({ provider, firstId, slots: slots?.map(slot => ({ ...slot })), items: Array.from(items).sort(([a], [b]) => a - b).map(([, item]) => item), loading, error: failed });
        };
        const finish = (timedOut = false) => {
            if (!active) return;
            publish(false);
            timing.finish(timedOut ? 'timeout' : failed ? 'error' : 'complete', items.size, slots?.length);
            active = false;
            clearTimeout(timer);
        };
        const timer = setTimeout(() => { failed = true; finish(true); }, LOAD_TIMEOUT_MS);
        Promise.resolve().then(() => provider.once()).then(resolvers => {
            if (!active) return;
            firstId = resolvers[0]?.id;
            slots = resolvers.map(resolver => ({ id: resolver.id }));
            timing.list();
            publish(true);
            return Promise.all(resolvers.map((resolver, index) =>
                Promise.resolve().then(() => resolver.asAPromise()).then(item => {
                    if (!active) return;
                    if (items.size === 0) timing.first();
                    if (slots) slots[index].item = item;
                    items.set(index, item);
                    publish(true);
                }, () => { failed = true; })
            ));
        }).then(() => finish(), () => { failed = true; finish(); });
        return () => {
            active = false;
            clearTimeout(timer);
            timing.finish('cancelled', items.size, slots?.length);
        };
    }, [provider, label, attempt]);

    if (!provider || state.provider !== provider) return { items: EMPTY_ITEMS as T[], loading: !!provider, error: false, firstId: undefined, slots: undefined, retry };
    return { ...state, retry };
}
